import {
  annotationToolApiV1,
  getAnnotationToolBasePath,
  type ListResponse,
  type ObjectResponse,
} from '@verantos-dev/core-annotation-tool';

import type { ManageProjectResponse } from '../../admin-projects/types/response';
import { CREATE_PROJECT_WIZARD_TAG } from '../constants';
import { AnnotationUser } from '../state/createProjectSlice';
import {
  AnnotationAgentAllocationPreviewRequest,
  AnnotationAgentConceptLabelsRequest,
  AnnotationAgentRenderPromptRequest,
  CreateAnnotationProjectRequest,
} from '../types/request';
import type {
  AllocationSummaryResponse,
  AnnotationAgentAllocationPreviewResponse,
  AnnotationAgentConceptLabelsResponse,
  AnnotationAgentRenderPromptResponse,
  AnnotationAgentSetupResponse,
  AttributeResponse,
  ConceptGroupResponse,
  ConditionAreaResponse,
  DataSplitSummaryResponse,
  OrganizationResponse,
} from '../types/response';

export const createProjectApiSlice = annotationToolApiV1
  .enhanceEndpoints({
    addTagTypes: [CREATE_PROJECT_WIZARD_TAG],
  })
  .injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
      getManageProjects: builder.query<ListResponse<ManageProjectResponse>, void>({
        query: () => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return `${tempPath}/manage-projects/projects`;
        },
        providesTags: [CREATE_PROJECT_WIZARD_TAG],
      }),

      getConditionAreas: builder.query<ListResponse<ConditionAreaResponse>, void>({
        query: () => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/manage-projects/conditions`;
        },
        providesTags: [CREATE_PROJECT_WIZARD_TAG],
      }),

      getOrganizations: builder.query<ListResponse<OrganizationResponse>, void>({
        query: () => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/manage-projects/organizations`;
        },
        providesTags: [CREATE_PROJECT_WIZARD_TAG],
      }),

      getConceptGroups: builder.query<ListResponse<ConceptGroupResponse>, { search?: string }>({
        query: ({ search }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/manage-projects/concept-groups?search=${search}`;
        },
        providesTags: [CREATE_PROJECT_WIZARD_TAG],
      }),

      getAttributes: builder.query<ListResponse<AttributeResponse>, void>({
        query: () => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/manage-projects/attributes`;
        },
        providesTags: [CREATE_PROJECT_WIZARD_TAG],
      }),

      getMandatoryAttributeNames: builder.query<ListResponse<string>, void>({
        query: () => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/manage-projects/mandatory-attribute-names`;
        },
        providesTags: [CREATE_PROJECT_WIZARD_TAG],
      }),

      createAnnotationProject: builder.mutation<
        ObjectResponse<{ projectId: string; status: string }>,
        CreateAnnotationProjectRequest
      >({
        query: ({
          basics,
          conceptGroups,
          annotators,
          supervisors,
          defaultAllocationMode,
          workflowId,
          annotationAgent,
        }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/project`,
            method: 'POST',
            body: {
              basics,
              conceptGroups,
              annotators,
              supervisors,
              defaultAllocationMode,
              workflowId,
              // TODO: cleanup with cp_annotation_createproject_agent ff
              ...(annotationAgent ? { annotationAgent } : {}),
            },
          };
        },
      }),

      createWorkflowId: builder.mutation<ObjectResponse<string>, void>({
        query: () => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/create-workflow-id`,
            method: 'POST',
          };
        },
      }),

      uploadNotesParquetFile: builder.mutation<any, { file: File; workflowId: string }>({
        query: ({ file, workflowId }) => {
          const formData = new FormData();
          formData.append('file', file);

          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/${workflowId}/notes-file`,
            method: 'POST',
            body: formData,
          };
        },
        invalidatesTags: [],
      }),

      uploadPersonParquetFile: builder.mutation<any, { file: File; workflowId: string }>({
        query: ({ file, workflowId }) => {
          const formData = new FormData();
          formData.append('file', file);

          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/${workflowId}/person-file`,
            method: 'POST',
            body: formData,
          };
        },
        invalidatesTags: [],
      }),

      uploadVisitParquetFile: builder.mutation<any, { file: File; workflowId: string }>({
        query: ({ file, workflowId }) => {
          const formData = new FormData();
          formData.append('file', file);

          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/${workflowId}/visit-file`,
            method: 'POST',
            body: formData,
          };
        },
        invalidatesTags: [],
      }),

      uploadDatasplittingJSONFile: builder.mutation<any, { file: File; workflowId: string }>({
        query: ({ file, workflowId }) => {
          const formData = new FormData();
          formData.append('file', file);

          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/${workflowId}/datasplitting-json`,
            method: 'POST',
            body: formData,
          };
        },
        invalidatesTags: [],
      }),

      getDataSplitSummary: builder.query<
        ObjectResponse<DataSplitSummaryResponse>,
        { workflowId: string }
      >({
        query: ({ workflowId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/manage-projects/${workflowId}/data-split-summary`;
        },
        providesTags: [CREATE_PROJECT_WIZARD_TAG],
      }),

      getAllocationSummary: builder.query<
        ListResponse<AllocationSummaryResponse>,
        { selectedAnnotators: AnnotationUser[]; defaultAllocationMode: boolean; workflowId: string }
      >({
        query: ({ selectedAnnotators, defaultAllocationMode, workflowId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/${workflowId}/allocation-summary`,
            method: 'POST',
            body: {
              selectedAnnotators,
              defaultAllocationMode,
            },
          };
        },
        providesTags: [CREATE_PROJECT_WIZARD_TAG],
      }),
      uploadNotesS3Path: builder.mutation<any, { s3Path: string; workflowId: string }>({
        query: ({ s3Path, workflowId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/${workflowId}/notes-s3-path`,
            method: 'POST',
            body: { s3Path },
          };
        },
        invalidatesTags: [],
      }),

      uploadPersonS3Path: builder.mutation<any, { s3Path: string; workflowId: string }>({
        query: ({ s3Path, workflowId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/${workflowId}/person-s3-path`,
            method: 'POST',
            body: { s3Path },
          };
        },
        invalidatesTags: [],
      }),

      uploadVisitS3Path: builder.mutation<any, { s3Path: string; workflowId: string }>({
        query: ({ s3Path, workflowId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/${workflowId}/visit-s3-path`,
            method: 'POST',
            body: { s3Path },
          };
        },
        invalidatesTags: [],
      }),

      uploadDatasplittingJSONS3Path: builder.mutation<any, { s3Path: string; workflowId: string }>({
        query: ({ s3Path, workflowId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/${workflowId}/datasplitting-json-s3-path`,
            method: 'POST',
            body: { s3Path },
          };
        },
        invalidatesTags: [],
      }),

      getCreateProjectStatus: builder.query<
        ObjectResponse<{ status: string }> & { httpStatus: number },
        { workflowId: string }
      >({
        query: ({ workflowId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/${workflowId}/status`,
            responseHandler: async (response) => {
              const body = await response.json().catch(() => ({}));
              return { httpStatus: response.status, ...body };
            },
          };
        },
      }),

      // TODO: cleanup with cp_annotation_createproject_agent ff
      getAnnotationAgentSetup: builder.query<ObjectResponse<AnnotationAgentSetupResponse>, void>({
        query: () => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return `${tempPath}/manage-projects/annotation-agent/setup`;
        },
      }),

      // TODO: cleanup with cp_annotation_createproject_agent ff
      // The server adds the label of a concept group that has none, so this POST writes; the
      // result for the same arguments is always the same, which makes it a query.
      getAnnotationAgentConceptLabels: builder.query<
        ObjectResponse<AnnotationAgentConceptLabelsResponse>,
        AnnotationAgentConceptLabelsRequest
      >({
        query: (body) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/annotation-agent/concept-labels`,
            method: 'POST',
            body,
          };
        },
      }),

      // TODO: cleanup with cp_annotation_createproject_agent ff
      // A POST that only reads: the server stores nothing, so it is a query keyed by its body.
      renderAnnotationAgentPrompt: builder.query<
        ObjectResponse<AnnotationAgentRenderPromptResponse>,
        AnnotationAgentRenderPromptRequest
      >({
        query: (body) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/annotation-agent/render-prompt`,
            method: 'POST',
            body,
          };
        },
      }),

      // TODO: cleanup with cp_annotation_createproject_agent ff
      // The server only caches the encounter count on the workflow, so this POST is a query
      // keyed by its body.
      getAnnotationAgentAllocationPreview: builder.query<
        ObjectResponse<AnnotationAgentAllocationPreviewResponse>,
        AnnotationAgentAllocationPreviewRequest
      >({
        query: ({ workflowId, mode, humans }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/${workflowId}/annotation-agent/allocation-preview`,
            method: 'POST',
            body: { mode, humans },
          };
        },
      }),
    }),
  });

export const {
  useGetManageProjectsQuery,
  useGetConditionAreasQuery,
  useGetOrganizationsQuery,
  useGetConceptGroupsQuery,
  useGetAttributesQuery,
  useCreateAnnotationProjectMutation,
  useCreateWorkflowIdMutation,
  useUploadNotesParquetFileMutation,
  useUploadPersonParquetFileMutation,
  useUploadVisitParquetFileMutation,
  useUploadDatasplittingJSONFileMutation,
  useGetDataSplitSummaryQuery,
  useGetAllocationSummaryQuery,
  useGetMandatoryAttributeNamesQuery,
  useUploadNotesS3PathMutation,
  useUploadPersonS3PathMutation,
  useUploadVisitS3PathMutation,
  useUploadDatasplittingJSONS3PathMutation,
  useGetCreateProjectStatusQuery,
  useGetAnnotationAgentSetupQuery,
  useGetAnnotationAgentConceptLabelsQuery,
  useRenderAnnotationAgentPromptQuery,
  useGetAnnotationAgentAllocationPreviewQuery,
} = createProjectApiSlice;

// getAnnotators/getSupervisors are defined in core-annotation-tool, which
// ui-annotation-tool's PeopleSection cluster depends on for the same data.
// Re-exported here so this module's existing consumers can keep importing
// these hooks from this path.
export { useGetAnnotatorsQuery, useGetSupervisorsQuery } from '@verantos-dev/core-annotation-tool';
